import type { AkpError, RouteKey } from '@akpl/shared';
import { type ApiClient, createApiClient } from '@akpl/shared';

import { getBridge } from './bridge';
import { currentLocale } from './i18n';

/** Where the API lives. Build-time default; the app config itself comes from `/v1/config`. */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? 'http://localhost:8787').replace(
  /\/$/,
  '',
);

export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? 'dev';

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider = async () => null;

/** Installed by the boot sequence once Firebase Auth is ready (docs/04 §1). */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

export type ApiErrorHandler = (error: AkpError, info: { route: RouteKey }) => void;

let apiErrorHandler: ApiErrorHandler | null = null;

/**
 * One app-wide hook for failed calls (after retries). WP1.6 installs the session guard here:
 * a `session_expired` answer ends the session exactly like `meta.status='closed'` does.
 */
export function setApiErrorHandler(handler: ApiErrorHandler | null): void {
  apiErrorHandler = handler;
}

const IS_TEST = import.meta.env.MODE === 'test';

/**
 * Typed client shared by every screen (`api.home()`, `api.search({ query: { q } })`, …).
 * Carries `Authorization` (TV custom-token session) and `X-Akapella-Device` automatically.
 */
export const api: ApiClient = createApiClient({
  baseUrl: API_BASE,
  getToken: () => tokenProvider(),
  getDeviceId: () => getBridge(APP_VERSION).deviceId,
  headers: () => ({ 'Accept-Language': currentLocale() }),
  // Resolve `fetch` per call (tests stub the global after this module is evaluated).
  fetch: (input, init) => globalThis.fetch(input, init),
  validateResponses: import.meta.env.DEV,
  onError: (error, info) => apiErrorHandler?.(error, { route: info.route }),
  retries: IS_TEST ? 0 : 3,
  retryBaseMs: IS_TEST ? 0 : 300,
  timeoutMs: IS_TEST ? 2000 : 15_000,
});

/**
 * The budget of the one call a cold TV cannot start without, `GET /config`.
 *
 * QA 2026-09-15 **UX-380**: against an API that accepts the socket and never answers — a flaky
 * living-room Wi-Fi, not the fast 503 the error screen was tested against — the app-wide
 * 4 attempts × 15 s left the room staring at "Iniciando…" for **63 s**. Boot gets its own,
 * much shorter budget (2 attempts × 7 s ≈ 14 s worst case) so the recoverable error screen
 * arrives while someone is still in the room; every other route keeps the generous default,
 * which is right for a call made while the app is already usable.
 */
export const BOOT_CONFIG_RETRIES = 1;
export const BOOT_CONFIG_TIMEOUT_MS = 7_000;

export const BOOT_CONFIG_BUDGET: { retries: number; timeoutMs: number } = IS_TEST
  ? { retries: 0, timeoutMs: 2000 }
  : { retries: BOOT_CONFIG_RETRIES, timeoutMs: BOOT_CONFIG_TIMEOUT_MS };
